/* eslint-disable react-hooks/exhaustive-deps */
import React, {
	useMemo,
	useState,
	useEffect,
	useContext,
	useCallback,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	MaterialReactTable,
	useMaterialReactTable,
} from 'material-react-table';
import {
	Box,
	Button,
	Chip,
	IconButton,
	Tooltip,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { useAppContext, ACTION_TYPES } from '../../Context/AppContext';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { makeApiRequest } from '../../Shared/Api';
import CreateJob from '../../Components/Modals/CreateJob';
import MobileCreateFab from '../../Components/MobileCreateFab';
import { useRealtimeEvent } from '../../Context/RealtimeContext';
import { applyBidRealtimeToJobList } from '../../Shared/bidRealtime';
import { BID_STATUS_COLORS, BID_STATUS_LABELS } from '../../Shared/bids';
import {
	DEFAULT_JOBS_PAGE_SIZE,
	buildJobsListParams,
	fetchJobsList,
} from '../../Shared/jobs';
import useJobStatuses from '../../Hooks/useJobStatuses';
import { JobDetailContext } from './context/JobDetailContext';
import { refreshOnboardingChecklist } from '../../Shared/onboarding';
import JobsMobileList from './components/JobsMobileList';

const ORDERING_FIELD_BY_COLUMN = {
	customer: 'customer_name',
};

const mrtOrdering = (sorting) => {
	const first = sorting?.[0];
	if (!first?.id) return undefined;
	const field = ORDERING_FIELD_BY_COLUMN[first.id] || first.id;
	return first.desc ? `-${field}` : field;
};

const JobListPage = () => {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { dispatch: appDispatch } = useAppContext();
	const { dispatch } = useContext(JobDetailContext);
	const {
		labels: statusLabels,
		colors: statusColors,
		options: jobStatusOptions,
	} = useJobStatuses();

	const [jobs, setJobs] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [customerOptions, setCustomerOptions] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isNewJobModalOpen, setIsNewJobModalOpen] = useState(false);
	const [mobileRefreshKey, setMobileRefreshKey] = useState(0);

	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: DEFAULT_JOBS_PAGE_SIZE,
	});
	const [sorting, setSorting] = useState([{ id: 'created_at', desc: true }]);
	const [columnFilters, setColumnFilters] = useState([]);
	const [globalFilter, setGlobalFilter] = useState('');
	const [debouncedGlobalFilter, setDebouncedGlobalFilter] = useState('');

	useEffect(() => {
		if (searchParams.get('newJob') === '1') {
			setIsNewJobModalOpen(true);
			setSearchParams({}, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	useEffect(() => {
		const timer = setTimeout(
			() => setDebouncedGlobalFilter(globalFilter || ''),
			300,
		);
		return () => clearTimeout(timer);
	}, [globalFilter]);

	useEffect(() => {
		appDispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Jobs' },
		});
	}, []);

	useEffect(() => {
		let cancelled = false;
		makeApiRequest('GET', '/customers/')
			.then((response) => {
				if (cancelled) return;
				setCustomerOptions(
					(response || [])
						.map((customer) => ({
							value: String(customer.id),
							label: customer.name,
						}))
						.sort((a, b) => a.label.localeCompare(b.label)),
				);
			})
			.catch(() => {
				if (!cancelled) setCustomerOptions([]);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const filterValues = useMemo(() => {
		const find = (id) =>
			columnFilters.find((filter) => filter.id === id)?.value || [];
		return {
			customers: find('customer'),
			bidStatuses: find('bid_status'),
			jobStatuses: find('job_status'),
		};
	}, [columnFilters]);

	const fetchDesktopJobs = useCallback(async () => {
		if (!isDesktop) return;
		setIsLoading(true);
		try {
			const params = buildJobsListParams({
				page: pagination.pageIndex + 1,
				pageSize: pagination.pageSize,
				search: debouncedGlobalFilter,
				customers: filterValues.customers,
				bidStatuses: filterValues.bidStatuses,
				jobStatuses: filterValues.jobStatuses,
				ordering: mrtOrdering(sorting),
			});
			const { results, count } = await fetchJobsList(params);
			setJobs(results);
			setRowCount(count);
		} catch (error) {
			console.error('Error fetching jobs:', error);
			setJobs([]);
			setRowCount(0);
		} finally {
			setIsLoading(false);
		}
	}, [
		isDesktop,
		pagination.pageIndex,
		pagination.pageSize,
		debouncedGlobalFilter,
		filterValues,
		sorting,
	]);

	useEffect(() => {
		fetchDesktopJobs();
	}, [fetchDesktopJobs]);

	useEffect(() => {
		setPagination((prev) =>
			prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
		);
	}, [
		debouncedGlobalFilter,
		filterValues.customers,
		filterValues.bidStatuses,
		filterValues.jobStatuses,
		sorting,
	]);

	const handleJobCreated = () => {
		if (isDesktop) {
			fetchDesktopJobs();
		} else {
			setMobileRefreshKey((key) => key + 1);
		}
		refreshOnboardingChecklist();
	};

	const handleRealtimeUpdate = useCallback(
		(message) => {
			if (isDesktop) {
				setJobs((prev) => applyBidRealtimeToJobList(prev, message));
			}
		},
		[isDesktop],
	);

	useRealtimeEvent(handleRealtimeUpdate);

	const openJob = (jobId) => {
		dispatch({ type: 'SET_SELECTED_JOB_ID', payload: jobId });
		navigate(`/jobs/view/${jobId}`);
	};

	const columns = useMemo(() => {
		const baseColumns = [
			{
				accessorKey: 'name',
				header: 'Job Name',
				size: 200,
				enableColumnFilter: false,
			},
			{
				accessorKey: 'customer',
				header: 'Customer',
				size: 150,
				filterVariant: 'multi-select',
				filterSelectOptions: customerOptions,
				Cell: ({ row }) => row.original.customer_name || row.original.customer,
			},
			{
				accessorKey: 'bid_status',
				header: 'Bid Status',
				size: 130,
				filterVariant: 'multi-select',
				filterSelectOptions: Object.entries(BID_STATUS_LABELS).map(
					([value, label]) => ({ value, label }),
				),
				Cell: ({ cell }) => {
					const status = cell.getValue();
					return (
						<Chip
							label={BID_STATUS_LABELS[status] || status}
							color={BID_STATUS_COLORS[status] || 'default'}
							size="small"
						/>
					);
				},
			},
			{
				accessorKey: 'bid_amount',
				header: 'Bid Amount',
				size: 130,
				enableColumnFilter: false,
				Cell: ({ cell }) => `$${cell.getValue()}`,
			},
		];

		baseColumns.push({
			accessorKey: 'job_status',
			header: 'Job Status',
			size: 130,
			filterVariant: 'multi-select',
			filterSelectOptions: jobStatusOptions,
			Cell: ({ cell }) => {
				const status = cell.getValue();
				return (
					<Chip
						label={statusLabels[status] || status}
						color={statusColors[status] || 'default'}
						size="small"
					/>
				);
			},
		});

		baseColumns.push(
			{
				accessorKey: 'due_date',
				header: 'Due Date',
				size: 120,
				enableColumnFilter: false,
				Cell: ({ cell }) => {
					const value = cell.getValue();
					return value ? new Date(value).toLocaleDateString() : '-';
				},
			},
			{
				accessorKey: 'created_at',
				header: 'Created At',
				size: 120,
				enableColumnFilter: false,
				Cell: ({ cell }) => new Date(cell.getValue()).toLocaleDateString(),
			},
		);

		return baseColumns;
	}, [statusLabels, statusColors, jobStatusOptions, customerOptions]);

	const renderNewJobButton = () => (
		<Button
			variant="contained"
			color="primary"
			startIcon={<AddIcon />}
			onClick={() => setIsNewJobModalOpen(true)}
			sx={{
				textTransform: 'none',
				fontWeight: 600,
			}}
		>
			New Job
		</Button>
	);

	const table = useMaterialReactTable({
		columns,
		data: jobs,
		layoutMode: 'semantic',
		manualPagination: true,
		manualFiltering: true,
		manualSorting: true,
		rowCount,
		enableColumnFilters: true,
		enableGlobalFilter: true,
		enableSorting: true,
		onPaginationChange: setPagination,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		onGlobalFilterChange: setGlobalFilter,
		initialState: {
			showGlobalFilter: true,
			sorting: [{ id: 'created_at', desc: true }],
			pagination: {
				pageIndex: 0,
				pageSize: DEFAULT_JOBS_PAGE_SIZE,
			},
		},
		state: {
			isLoading,
			pagination,
			sorting,
			columnFilters,
			globalFilter,
		},
		muiTableBodyRowProps: ({ row }) => ({
			onClick: () => openJob(row.original.id),
			sx: {
				cursor: 'pointer',
			},
		}),
		renderRowActions: ({ row }) => (
			<Box sx={{ display: 'flex', gap: '0.5rem' }}>
				<Tooltip title="View Details">
					<IconButton
						color="info"
						onClick={(e) => {
							e.stopPropagation();
							openJob(row.original.id);
						}}
					>
						<VisibilityIcon />
					</IconButton>
				</Tooltip>
			</Box>
		),
		renderTopToolbarCustomActions: renderNewJobButton,
		muiTablePaperProps: {
			elevation: 2,
			sx: {
				borderRadius: '8px',
			},
		},
		muiTableHeadCellProps: {
			sx: {
				fontWeight: 'bold',
				fontSize: '0.95rem',
			},
		},
	});

	return (
		<Box sx={{ width: '100%' }}>
			{isDesktop ? (
				<MaterialReactTable table={table} />
			) : (
				<>
					<JobsMobileList
						onJobClick={openJob}
						showJobStatus
						statusLabels={statusLabels}
						statusColors={statusColors}
						jobStatusOptions={jobStatusOptions}
						refreshKey={mobileRefreshKey}
					/>
					<MobileCreateFab
						label="New Job"
						onClick={() => setIsNewJobModalOpen(true)}
					/>
				</>
			)}
			<CreateJob
				open={isNewJobModalOpen}
				onClose={() => setIsNewJobModalOpen(false)}
				onJobCreated={handleJobCreated}
			/>
		</Box>
	);
};

export default JobListPage;
